import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, httpResource } from '@angular/common/http';
import { Toolchain, ToolchainRegistration } from '../../../utils/models/toolchain.models';
import { Log } from '../../../utils/models/tool.models';
import { firstValueFrom } from 'rxjs';

/**
 * The service for interacting with toolchains.
 */
@Injectable({
  providedIn: 'root',
})
export class ToolchainService {
  /** Inject services. */
  private readonly httpService = inject(HttpClient);

  /**
   * Retrieves a list of all toolchains including internal/admin-visible ones.
   */
  getToolchainDescriptionsAdmin() {
    return httpResource<Toolchain[]>(() => 'internal/toolchains');
  }

  /**
   * Uploads a toolchain specification to the backend.
   */
  uploadToolchain(toolchainConfig: FormData) {
    const res = this.httpService.post<Log>('internal/toolchains', toolchainConfig);
    return firstValueFrom(res);
  }

  /**
   * Retrieves all toolchain registrations for the current user or admin view.
   */
  getRegistrations() {
    return httpResource<ToolchainRegistration[]>(() => `internal/toolchains/registrations`);
  }

  /**
   * Registers a user to a toolchain.
   */
  updateRegistration(userId: number, toolchainId: number) {
    const payload = {
      registrations: [{ user_id: userId, toolchain_id: toolchainId }],
    };
    const res = this.httpService.post<{ result: string }>(
      `internal/toolchains/registrations`,
      payload
    );
    return firstValueFrom(res);
  }

  /**
   * Removes a user registration from a toolchain.
   */
  deleteRegistration(userId: number, toolchainId: number) {
    const payload = {
      registrations: [{ user_id: userId, toolchain_id: toolchainId }],
    };
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: payload,
    };
    const res = this.httpService.delete<{ result: string }>(
      `internal/toolchains/registrations`,
      options
    );
    return firstValueFrom(res);
  }
}
